/**
 * NAO checkout — punto de entrada / orquestador.
 *
 * Arquitectura (ver CLAUDE.md §19): el checkout custom se divide en módulos ES
 * cargados vía el importmap `@theme/*` de snippets/scripts.liquid:
 *   - @theme/nao-form-config          configuración por producto + reglas de envío
 *   - @theme/nao-checkout-data        datos y constantes (colombiaData, LS)
 *   - @theme/nao-checkout-validation  validación de campos
 *   - @theme/nao-checkout-api         contrato con el backend (payload + fetch)
 *   - @theme/nao-checkout-storage     borrador en localStorage
 *   - @theme/nao-checkout-ui          totales, método de pago, cantidad, ciudades
 *   - @theme/nao-checkout-aromas      selector de aromas con cantidad por aroma
 *   - @theme/nao-checkout-modal       apertura/cierre, foco y modal de éxito
 *   - @theme/nao-checkout-variant     variante elegida en el picker nativo + stock
 *
 * Este entry arma un contexto compartido (`ctx`) con el estado por instancia
 * (elements, config del producto, precio, formatter…) y cablea los eventos.
 *
 * Hay dos flujos y comparten TODO menos el envoltorio: con ventana emergente
 * (`usaModal: true`, hoy el de todos los productos) y embebido en la página
 * (`usaModal: false`, sin producto que lo use hoy, pero sostenido porque es el
 * que sigue funcionando si el JS no carga). Los listeners de modal solo se
 * cablean si el modal existe.
 */

import { configFromWrapper } from '@theme/nao-form-config';
import { colombiaData, buildLsKey } from '@theme/nao-checkout-data';
import { validateField } from '@theme/nao-checkout-validation';
import { buildPayload, submitOrder } from '@theme/nao-checkout-api';
import { saveToLocalStorage, loadSavedData } from '@theme/nao-checkout-storage';
import { applySelection, populateCities, handlePhoneSync, recalculate, currentQuantity } from '@theme/nao-checkout-ui';
import { openModal, closeModal, trapFocus, showSuccessModal } from '@theme/nao-checkout-modal';
import { setupAromas, aromaLines, aromaSubtotal, hasAromas } from '@theme/nao-checkout-aromas';
import { setupVariantSync } from '@theme/nao-checkout-variant';

// Inicializa todos los wrappers de checkout dentro de `root` (documento o
// sección recargada por el Theme Editor). La guarda por instancia evita la
// doble inicialización cuando Shopify vuelve a emitir shopify:section:load.
function initAll(root) {
    (root || document)
        .querySelectorAll('.nao-checkout-wrapper')
        .forEach(initCheckout);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
} else {
    initAll(document);
}

// Theme Editor: al editar/recargar la sección, el DOM del bloque se
// reemplaza y hay que re-adjuntar los listeners sobre el nuevo nodo.
document.addEventListener('shopify:section:load', (event) => initAll(event.target));

function initCheckout(wrapper) {

    // Guarda por instancia: no reinicializar un wrapper ya cableado.
    if (wrapper.dataset.naoInit === 'true') return;
    wrapper.dataset.naoInit = 'true';

    // Configuración del producto (metacampo custom.nao_form_key). Una clave
    // desconocida cae en `_default`: formulario funcional y sin promo de gotero.
    const config = configFromWrapper(wrapper);

    // =========================
    // ELEMENTOS DOM
    // =========================

    const elements = {
        ctaButton: wrapper.querySelector('.js-nao-cta'),
        modal: wrapper.querySelector('.js-nao-modal'),
        closeModalBtn: wrapper.querySelector('.js-nao-close'),

        form: wrapper.querySelector('.js-nao-form'),

        provinceSelect: wrapper.querySelector('.js-nao-province'),
        citySelect: wrapper.querySelector('.js-nao-city'),

        submitBtn: wrapper.querySelector('.js-nao-submit'),

        shippingMethods: wrapper.querySelectorAll('.js-nao-method'),
        methodPrices: wrapper.querySelectorAll('.js-nao-method-price'),

        summarySubtotal: wrapper.querySelector('.js-nao-summary-subtotal'),
        summaryShipping: wrapper.querySelector('.js-nao-summary-shipping'),
        summaryTotal: wrapper.querySelector('.js-nao-summary-total'),

        shippingCostInput: wrapper.querySelector('.js-nao-shipping-cost'),

        // Selector de aromas: la cantidad del pedido es la suma de
        // estas filas, y cada una se convierte en una línea del pedido.
        aromaRows: wrapper.querySelectorAll('.js-nao-aroma'),
        aromaCount: wrapper.querySelector('.js-nao-aroma-count'),
        aromaEmpty: wrapper.querySelector('.js-nao-aroma-empty'),
        variantTitle: wrapper.querySelector('.js-nao-variant-title'),

        // Aviso de envío escalonado y desplegable del formulario embebido.
        freeNote: wrapper.querySelector('.js-nao-free-note'),
        disclosure: wrapper.querySelector('.js-nao-disclosure'),

        qtyInput: wrapper.querySelector('.js-nao-qty'),
        qtyBadge: wrapper.querySelector('.js-nao-qty-badge'),
        qtyMinus: wrapper.querySelector('.js-nao-qty-minus'),
        qtyPlus: wrapper.querySelector('.js-nao-qty-plus'),
        unitPrice: wrapper.querySelector('.js-nao-unit-price'),

        // Donde aterrizan los fallos de envio del pedido, en vez de un alert().
        formError: wrapper.querySelector('.js-nao-form-error'),

        lossAlert: wrapper.querySelector('.js-nao-loss-alert'),
        lossModal: wrapper.querySelector('.js-nao-loss-modal'),

        btnSwitchPrepaid: wrapper.querySelector('.js-nao-loss-switch'),
        btnContinueCOD: wrapper.querySelector('.js-nao-loss-continue'),

        shippingPhone: wrapper.querySelector('.js-nao-shipping-phone'),
        whatsappPhone: wrapper.querySelector('.js-nao-whatsapp-phone'),
        whatsappCheckbox: wrapper.querySelector('.js-nao-same-phone'),

        basePriceContainer: wrapper.querySelector('.js-nao-base-price'),

        btnMainText: wrapper.querySelector('.nao-btn-main-text'),
        btnSubText: wrapper.querySelector('.nao-btn-sub-text')
    };

    const PRODUCT_PRICE = elements.basePriceContainer
        ? parseInt(elements.basePriceContainer.dataset.price) / 100
        : 129900;

    const formatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0
    });

    const lsKey = buildLsKey(wrapper.dataset.naoFormKey);

    // Contexto compartido que reciben los módulos. Las referencias cruzadas
    // (p. ej. UI → storage) se resuelven mediante estos wrappers para que cada
    // módulo no necesite conocer a los demás directamente.
    const ctx = {
        wrapper,
        config,
        elements,
        PRODUCT_PRICE,
        formatter,
        colombiaData,
        lsKey,
        isCOD: false,
        quantity: 1,
        shippingCost: 0,
        lastFocusedElement: null
    };

    ctx.saveToLocalStorage = () => saveToLocalStorage(ctx);
    ctx.populateCities = (department) => populateCities(ctx, department);
    ctx.handlePhoneSync = () => handlePhoneSync(ctx);

    // =========================
    // EVENTOS
    // =========================

    // Abrir modal (solo en el flujo con ventana emergente).
    if (elements.ctaButton && elements.modal) {

        elements.ctaButton.addEventListener('click', () => {

            // Color agotado: el aviso ya esta debajo del boton; abrir el
            // formulario solo llevaria a rellenarlo para nada.
            if (ctx.variantSoldOut) {

                const aviso = wrapper.querySelector('.js-nao-soldout');

                if (aviso) {
                    aviso.hidden = false;
                }

                return;
            }

            openModal(ctx);
        });
    }

    // Cerrar modal
    if (elements.closeModalBtn) {

        elements.closeModalBtn.addEventListener(
            'click',
            () => closeModal(ctx)
        );
    }

    // Click fuera modal
    if (elements.modal) {

        elements.modal.addEventListener('click', event => {

            if (event.target === elements.modal) {
                closeModal(ctx);
            }
        });

        // Teclado: Escape cierra, Tab queda atrapado dentro del diálogo.
        elements.modal.addEventListener('keydown', event => {

            if (elements.modal.classList.contains('nao-active')) {
                trapFocus(event, elements.modal, () => closeModal(ctx));
            }
        });
    }

    // Teclado en el modal de pérdida: Escape equivale a "continuar con COD".
    if (elements.lossModal) {

        elements.lossModal.addEventListener('keydown', event => {

            if (
                event.key === 'Escape' &&
                elements.lossModal.classList.contains('nao-active')
            ) {
                event.preventDefault();
                elements.lossModal.classList.remove('nao-active');
            }
        });
    }

    // Aromas: cada cambio recalcula el resumen. No se guardan en el borrador
    // porque son la primera decisión de la página, siempre a la vista, y
    // restaurarlos podría resucitar una variante que ya no existe.
    setupAromas(ctx, () => recalculate(ctx));

    // Picker nativo de Horizon → formulario. Sin esto el pedido viaja con la
    // variante impresa al cargar la página, elija el cliente lo que elija.
    setupVariantSync(ctx, () => recalculate(ctx));

    // Desplegable del formulario embebido: al abrirlo, el foco (y la vista) van
    // al primer campo. Es `<details>`, así que sin JS sigue abriendo igual.
    if (elements.disclosure) {

        elements.disclosure.addEventListener('toggle', () => {

            if (elements.disclosure.open) {
                focusFirstField(ctx);
            }
        });
    }

    // Cantidad: los dos botones y la escritura directa recalculan el resumen.
    // El envío se actualiza solo al cruzar el umbral, sin banner ni animación.
    if (elements.qtyInput) {

        const stepQuantity = (delta) => {

            const next = currentQuantity(ctx) + delta;

            elements.qtyInput.value = String(Math.max(1, next));

            recalculate(ctx);

            saveToLocalStorage(ctx);
        };

        if (elements.qtyMinus) {
            elements.qtyMinus.addEventListener('click', () => stepQuantity(-1));
        }

        if (elements.qtyPlus) {
            elements.qtyPlus.addEventListener('click', () => stepQuantity(1));
        }

        elements.qtyInput.addEventListener('input', () => recalculate(ctx));

        // Al salir del campo se normaliza el valor (vacío o basura vuelve a 1).
        elements.qtyInput.addEventListener('change', () => {
            recalculate(ctx);
            saveToLocalStorage(ctx);
        });
    }

    // Provincias
    if (elements.provinceSelect) {

        Object.keys(colombiaData)
            .sort()
            .forEach(department => {

                const option =
                    document.createElement('option');

                option.value = department;
                option.textContent = department;

                elements.provinceSelect.appendChild(option);
            });

        elements.provinceSelect.addEventListener(
            'change',
            event => {

                populateCities(ctx, event.target.value);

                saveToLocalStorage(ctx);
            }
        );
    }

    // Métodos de envío / pago (patrón radiogroup accesible por teclado)
    function activateMethod(method) {

        const cost =
            parseInt(method.dataset.value) || 0;

        const isCOD = method.id === 'nao-method-cod';

        // El modal de aversión a la pérdida solo existe donde hay regalo que
        // perder; en el resto de productos ni se renderiza.
        if (
            isCOD &&
            config.regaloGotero &&
            elements.lossModal &&
            !sessionStorage.getItem(lossShownKey(ctx))
        ) {

            const lossTotalVal =
                wrapper.querySelector(
                    '.js-nao-loss-total-val'
                );

            if (lossTotalVal) {

                lossTotalVal.textContent =
                    formatter.format(
                        ctx.PRODUCT_PRICE * currentQuantity(ctx) + cost
                    );
            }

            elements.lossModal.classList.add(
                'nao-active'
            );

            // Mover el foco al diálogo de pérdida para permitir cierre por teclado.
            if (elements.btnSwitchPrepaid) {
                elements.btnSwitchPrepaid.focus();
            }

            sessionStorage.setItem(
                lossShownKey(ctx),
                'true'
            );
        }

        applySelection(
            ctx,
            method,
            cost,
            isCOD
        );
    }

    const methodList = Array.from(elements.shippingMethods);

    methodList.forEach((method, index) => {

        method.addEventListener('click', () => activateMethod(method));

        method.addEventListener('keydown', event => {

            // Enter/Espacio: seleccionar el método enfocado.
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault();
                activateMethod(method);
                return;
            }

            // Flechas: mover el foco entre radios (roving tabindex).
            let nextIndex = null;

            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                nextIndex = (index + 1) % methodList.length;
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                nextIndex = (index - 1 + methodList.length) % methodList.length;
            }

            if (nextIndex !== null) {
                event.preventDefault();
                const nextMethod = methodList[nextIndex];
                activateMethod(nextMethod);
                nextMethod.focus();
            }
        });
    });

    // Modal pérdida
    if (elements.btnSwitchPrepaid) {

        elements.btnSwitchPrepaid.addEventListener(
            'click',
            () => {

                elements.lossModal.classList.remove(
                    'nao-active'
                );

                const prepaidMethod =
                    wrapper.querySelector(
                        '#nao-method-prepaid'
                    );

                if (prepaidMethod) {

                    applySelection(
                        ctx,
                        prepaidMethod,
                        0,
                        false
                    );
                }
            }
        );
    }

    if (elements.btnContinueCOD) {

        elements.btnContinueCOD.addEventListener(
            'click',
            () => {

                elements.lossModal.classList.remove(
                    'nao-active'
                );
            }
        );
    }

    // Sync WhatsApp
    if (elements.whatsappCheckbox) {

        elements.whatsappCheckbox.addEventListener(
            'change',
            () => {

                handlePhoneSync(ctx);

                saveToLocalStorage(ctx);
            }
        );
    }

    if (elements.shippingPhone) {

        elements.shippingPhone.addEventListener(
            'input',
            () => {

                if (
                    elements.whatsappCheckbox &&
                    elements.whatsappCheckbox.checked
                ) {

                    elements.whatsappPhone.value =
                        elements.shippingPhone.value;

                    validateField(
                        elements.whatsappPhone
                    );
                }
            }
        );
    }

    // Inputs + submit
    if (elements.form) {

        loadSavedData(ctx);

        // La casilla de "mi WhatsApp es el mismo" viene marcada, así que hay
        // que aplicar la sincronización desde el arranque; si no, el campo de
        // WhatsApp queda editable y vacío contradiciendo a la casilla.
        handlePhoneSync(ctx);

        // Primer cálculo: deja el resumen y el input de envío coherentes desde
        // el arranque, también en el flujo inline donde no hay "abrir modal"
        // que dispare el primer render.
        recalculate(ctx);

        elements.form.addEventListener(
            'input',
            event => {

                const target = event.target;

                if (
                    target.tagName === 'INPUT' ||
                    target.tagName === 'SELECT'
                ) {

                    validateField(target);

                    saveToLocalStorage(ctx);
                }
            }
        );

        // =========================
        // SUBMIT
        // =========================

        elements.form.addEventListener(
            'submit',
            async event => {

                event.preventDefault();

                clearFormError(ctx);

                // Honeypot
                const honeypot =
                    elements.form.querySelector(
                        '.nao-honeypot'
                    );

                if (
                    honeypot &&
                    honeypot.value !== ''
                ) {
                    return;
                }

                // Color agotado: no se crea el pedido. Sin add-to-cart nativo
                // esta es la unica comprobacion de stock que existe.
                if (ctx.variantSoldOut) {

                    const aviso = wrapper.querySelector('.js-nao-soldout');

                    if (aviso) {
                        aviso.hidden = false;
                        aviso.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }

                    return;
                }

                // Con selector de aromas el pedido puede estar a cero. Antes de
                // validar direcciones, exigir que haya algo que despachar.
                if (hasAromas(ctx) && currentQuantity(ctx) < 1) {

                    if (elements.aromaEmpty) {
                        elements.aromaEmpty.hidden = false;
                    }

                    const primerAroma = wrapper.querySelector('.js-nao-aroma-qty');

                    if (primerAroma) {
                        primerAroma.focus();
                    }

                    return;
                }

                // Validar campos
                const inputs =
                    elements.form.querySelectorAll(
                        'input[required], select[required]'
                    );

                let isValid = true;

                inputs.forEach(input => {

                    if (!validateField(input)) {
                        isValid = false;
                    }
                });

                if (!isValid) return;

                // Loading UI
                const loader =
                    elements.submitBtn.querySelector(
                        '.nao-btn-loader'
                    );

                if (loader) {
                    loader.classList.remove(
                        'nao-hidden'
                    );
                }

                elements.submitBtn.disabled = true;

                try {

                    // Recalcular justo antes de enviar: el total que viaja es
                    // exactamente el que el cliente tiene delante.
                    recalculate(ctx);

                    const formData =
                        new FormData(elements.form);

                    const lineasAroma = aromaLines(ctx);

                    const payload = buildPayload(formData, {
                        shippingCost: ctx.shippingCost,
                        quantity: ctx.quantity,
                        isCOD: ctx.isCOD,
                        unitPrice: ctx.PRODUCT_PRICE,
                        // Con aromas el subtotal se suma línea a línea: el
                        // backend compara `expected_total` con su propio
                        // cálculo y rechaza con 409 si no cuadran.
                        items: lineasAroma,
                        subtotal: lineasAroma.length ? aromaSubtotal(ctx) : undefined,
                        honeypot: honeypot ? honeypot.value : ''
                    });

                    const result =
                        await submitOrder(payload);

                    // FIX #3: Si hay checkout_url (pago anticipado), redirigir
                    if (result.checkout_url) {
                        window.location.href = result.checkout_url;
                        return;
                    }

                    if (!result.success) {

                        throw new Error(
                            result.error ||
                            'Error al crear pedido'
                        );
                    }

                    // Tracking Meta (nao-meta-events.js escucha este evento).
                    // Solo contraentrega llega aquí: la orden ya quedó creada.
                    // Nunca debe poder bloquear la venta → try/catch aislado.
                    try {
                        wrapper.dispatchEvent(new CustomEvent('nao:purchase', {
                            bubbles: true,
                            detail: {
                                total: payload.expected_total,
                                shippingCost: ctx.shippingCost,
                                quantity: ctx.quantity,
                                paymentMethod: payload.payment_method,
                                orderNumber: result.order_number || '',
                                variantId: payload.variant_id
                            }
                        }));
                    } catch (trackingError) {
                        // Tracking opcional: se ignora cualquier fallo.
                    }

                    // =========================
                    // CONFIRMACIÓN
                    // =========================

                    // En el flujo con modal hay que cerrarlo antes de mostrar la
                    // confirmación; en el inline no hay nada que cerrar.
                    closeModal(ctx);

                    showSuccessModal(result.order_number, {
                        firstName: formData.get('full_name') || '',
                        // El aroma solo se confirma por WhatsApp donde el
                        // cliente todavía no lo eligió (Kit de Inicio). En el
                        // roll-on el aroma ya viene en la variante.
                        confirmarAroma: !config.selectorAroma
                    });

                    // Limpiar cache
                    localStorage.removeItem(lsKey);

                    // Re-habilitar botón submit para si el usuario abre el modal de nuevo
                    if (loader) {
                        loader.classList.add('nao-hidden');
                    }
                    elements.submitBtn.disabled = false;

                } catch (error) {

                    // El detalle técnico va a la consola; al cliente se le dice
                    // qué hacer. Antes esto era un alert() del navegador: un
                    // diálogo que bloquea la página, tapa el formulario y no
                    // deja ni releer lo que había escrito.
                    console.error(
                        'Error NAO Checkout:',
                        error
                    );

                    showFormError(
                        ctx,
                        'No pudimos crear tu pedido. Revisa tu conexión e inténtalo otra vez. ' +
                        'Si vuelve a fallar, escríbenos por WhatsApp y lo tomamos por ahí.'
                    );

                    const loader =
                        elements.submitBtn.querySelector(
                            '.nao-btn-loader'
                        );

                    if (loader) {

                        loader.classList.add(
                            'nao-hidden'
                        );
                    }

                    elements.submitBtn.disabled = false;
                }
            }
        );
    }
}

// La marca de "ya mostré el modal de pérdida" va namespaceada por producto: si
// dos checkouts conviven en la misma sesión, uno no silencia al otro.
function lossShownKey(ctx) {
    return 'nao_loss_shown_' + (ctx.wrapper.dataset.naoFormKey || 'default');
}

// Fallo al crear el pedido. El mensaje se enfoca (el contenedor es
// `tabindex="-1"` con `role="alert"`) para que el lector de pantalla lo
// anuncie y para que en móvil quede a la vista sin buscarlo.
function showFormError(ctx, message) {

    const box = ctx.elements.formError;

    if (!box) return;

    box.textContent = message;
    box.hidden = false;
    box.focus();
}

function clearFormError(ctx) {

    const box = ctx.elements.formError;

    if (!box) return;

    box.textContent = '';
    box.hidden = true;
}

/**
 * Lleva el foco y la vista al primer campo del formulario al desplegarlo.
 *
 * El foco va primero y con `preventScroll`, y el desplazamiento se pide aparte:
 * si se enfoca sin más, el salto instantáneo del navegador se come el scroll
 * suave. Se respeta `prefers-reduced-motion`.
 *
 * Enfocar el campo es además lo que dispara `InitiateCheckout` en el píxel
 * (nao-meta-events.js escucha `focusin` sobre los campos del formulario).
 */
function focusFirstField(ctx) {

    const form = ctx.elements.form;

    if (!form) return;

    const field = form.querySelector(
        'input:not([type="hidden"]):not(.nao-honeypot), select'
    );

    if (!field) return;

    const reducedMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    field.focus({ preventScroll: true });

    field.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center'
    });
}
